/**
 * Character Ban Node - Player View
 *
 * Tactical elimination phase where teams take turns banning characters.
 * Red/danger-themed atmospheric effects with dramatic ban animations.
 * Modeled after map-ban's quality bar with character-specific styling.
 */

import { getTeamNames } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Ban, X } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import { getTeamColor } from "../../lib/team-colors";
import type { NodePlayerViewProps } from "../types";

type Character = {
  id: string;
  name: string;
  imageUrl?: string;
};

type CharacterBanState = {
  characterPool: Character[];
  bannedCharacters: string[];
  bansByTeam: Record<number, string[]>;
  currentTeam: number;
  currentBanIndex: number;
  totalBansRequired: number;
};

export function CharacterBanView({
  nodeState,
  players: _players,
  context,
  onAction,
}: NodePlayerViewProps<CharacterBanState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as CharacterBanState;
  const config = nodeState.config as { labels?: { title?: string } };
  const title = config.labels?.title ?? "Character Ban Phase";
  const teams = context.teams as Record<string, number> | undefined;

  const myTeam = teams?.[userId || ""] ?? 0;
  const isMyTurn = myTeam === state.currentTeam;

  // Seeded team names
  const teamNames = useMemo(
    () => getTeamNames(context.matchId, context.teamCount),
    [context.matchId, context.teamCount],
  );
  const getTeamName = (teamIndex: number) => teamNames[teamIndex] ?? `Team ${teamIndex + 1}`;

  const handleBan = async (characterId: string) => {
    if (!isMyTurn || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    const result = await onAction("ban", { characterId, userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  const remainingCharacters = state.characterPool.filter(
    (c) => !state.bannedCharacters.includes(c.id),
  );

  return (
    <NodeCard accent="red">
      <NodeHeader
        title={title}
        accent="red"
        subtitle={
          <AnimatePresence mode="wait">
            <motion.p
              key={isMyTurn ? "your-turn" : `team-${state.currentTeam}`}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="text-sm @sm:text-base"
            >
              {isMyTurn ? (
                <span className="text-red-400 font-semibold">Your team&apos;s turn to ban</span>
              ) : (
                <span className="text-text-secondary">
                  Waiting for {getTeamName(state.currentTeam)} to ban&hellip;
                </span>
              )}
            </motion.p>
          </AnimatePresence>
        }
      />

      {/* Progress pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <Ban size={16} className="text-red-400" aria-hidden="true" />
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {state.currentBanIndex}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {state.totalBansRequired}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            bans
          </span>
          <div className="w-px h-5 bg-surface-700/60" />
          <span className="font-display text-lg font-bold text-brand-400 tabular-nums">
            {remainingCharacters.length}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            remain
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Character grid */}
      <div
        className={cn(
          "grid gap-3 @md:gap-4",
          state.characterPool.length <= 6
            ? "grid-cols-2 @lg:grid-cols-3"
            : state.characterPool.length <= 9
              ? "grid-cols-3 @lg:grid-cols-3 @3xl:grid-cols-4"
              : state.characterPool.length <= 16
                ? "grid-cols-3 @3xl:grid-cols-4 @3xl:grid-cols-5"
                : "grid-cols-4 @xl:grid-cols-5 @3xl:grid-cols-6",
        )}
      >
        {state.characterPool.map((char, index) => {
          const isBanned = state.bannedCharacters.includes(char.id);
          const bannedByTeam = Object.entries(state.bansByTeam).find(([, bans]) =>
            bans.includes(char.id),
          )?.[0];

          return (
            <CharacterBanCard
              key={char.id}
              character={char}
              isBanned={isBanned}
              bannedByTeam={bannedByTeam != null ? Number(bannedByTeam) : null}
              bannedByTeamName={bannedByTeam != null ? getTeamName(Number(bannedByTeam)) : null}
              myTeam={myTeam}
              isMyTurn={isMyTurn}
              disabled={isSubmitting || !isMyTurn || isBanned}
              index={index}
              onBan={handleBan}
            />
          );
        })}
      </div>

      {/* Team bans summary */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className={cn(
          "mt-6 grid gap-3 @md:gap-4",
          Object.keys(state.bansByTeam).length <= 2
            ? "grid-cols-1 @md:grid-cols-2"
            : Object.keys(state.bansByTeam).length === 3
              ? "grid-cols-1 @lg:grid-cols-3"
              : "grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-4",
        )}
      >
        {Object.entries(state.bansByTeam).map(([team, bans]) => {
          const teamNum = Number(team);
          const isMyTeamRow = teamNum === myTeam;
          const isBanning = teamNum === state.currentTeam;
          const teamColor = getTeamColor(teamNum, myTeam);
          return (
            <div
              key={team}
              className={cn(
                "rounded-lg p-4 border transition-shadow duration-500",
                isBanning
                  ? "bg-surface-800/60 border-red-500/30 shadow-[0_0_16px_rgba(239,68,68,0.06)]"
                  : isMyTeamRow
                    ? "bg-surface-800/60 border-surface-600/40"
                    : `bg-surface-800/40 ${teamColor.border}`,
              )}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className={cn(
                    "text-xs font-display font-semibold uppercase tracking-wider",
                    isBanning
                      ? "text-red-400"
                      : isMyTeamRow
                        ? "text-text-primary"
                        : "text-text-secondary",
                  )}
                >
                  {getTeamName(teamNum)}
                </span>
                {isMyTeamRow && (
                  <span className="text-[10px] uppercase tracking-wider text-brand-400 font-medium">
                    (you)
                  </span>
                )}
                {isBanning && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-[10px] uppercase tracking-wider text-red-400 font-semibold px-2 py-0.5 rounded bg-red-500/15 border border-red-500/20"
                  >
                    Banning
                  </motion.span>
                )}
                <div className="flex-1 h-px bg-surface-700/40" />
                <span className="text-xs text-surface-500 tabular-nums">
                  {bans.length} ban{bans.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 min-h-[28px]">
                <AnimatePresence>
                  {bans.length > 0 ? (
                    bans.map((charId) => {
                      const char = state.characterPool.find((c) => c.id === charId);
                      return (
                        <motion.span
                          key={charId}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium rounded-md max-w-full"
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="text-red-500/70 shrink-0"
                            aria-hidden="true"
                          >
                            <path
                              d="M18 6L6 18M6 6l12 12"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="truncate">{char?.name ?? charId}</span>
                        </motion.span>
                      );
                    })
                  ) : (
                    <span className="text-xs text-surface-500 italic">No bans yet</span>
                  )}
                </AnimatePresence>
              </div>
            </div>
          );
        })}
      </motion.div>
    </NodeCard>
  );
}

// ---------------------------------------------------------------------------
// Character ban card component
// ---------------------------------------------------------------------------

type CharacterBanCardProps = {
  character: Character;
  isBanned: boolean;
  bannedByTeam: number | null;
  bannedByTeamName: string | null;
  myTeam: number;
  isMyTurn: boolean;
  disabled: boolean;
  index: number;
  onBan: (characterId: string) => void;
};

function CharacterBanCard({
  character,
  isBanned,
  bannedByTeam,
  bannedByTeamName,
  myTeam,
  isMyTurn,
  disabled,
  index,
  onBan,
}: CharacterBanCardProps) {
  const delay = Math.min(0.1 + index * 0.05, 0.5);

  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
      whileHover={!disabled ? { scale: 1.03 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      onClick={() => onBan(character.id)}
      disabled={disabled}
      className={cn(
        "group relative rounded-lg overflow-hidden text-left",
        "transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-surface-900",
        isBanned
          ? "ring-1 ring-red-500/20 cursor-default"
          : isMyTurn
            ? "ring-1 ring-surface-700/50 hover:ring-red-500/50 cursor-pointer"
            : "ring-1 ring-surface-700/50 cursor-not-allowed opacity-80",
      )}
    >
      {/* Character portrait */}
      <div className="relative aspect-[3/4] bg-surface-800 overflow-hidden">
        {character.imageUrl ? (
          <img
            src={character.imageUrl}
            alt={character.name}
            className={cn(
              "w-full h-full object-cover transition-all duration-300",
              isBanned
                ? "brightness-[0.3] saturate-0 grayscale"
                : isMyTurn
                  ? "brightness-75 saturate-90 group-hover:brightness-90 group-hover:saturate-100"
                  : "brightness-60 saturate-75",
            )}
          />
        ) : (
          <div
            className={cn(
              "w-full h-full flex items-center justify-center transition-colors duration-300",
              isBanned
                ? "bg-surface-800 text-surface-600"
                : "bg-surface-800 text-surface-500 group-hover:text-surface-400",
            )}
          >
            <span className="font-display text-3xl @sm:text-4xl font-bold">
              {character.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {/* Bottom gradient for text readability */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Hover glow */}
        {!isBanned && isMyTurn && (
          <div className="absolute inset-0 bg-red-500/0 group-hover:bg-red-500/10 transition-colors duration-200" />
        )}

        {/* Character name - over portrait bottom */}
        <div className="absolute inset-x-0 bottom-0 p-2.5 @sm:p-3">
          <p
            className={cn(
              "font-display font-bold text-sm @sm:text-base leading-tight truncate",
              isBanned
                ? "text-surface-400 line-through decoration-red-500/60 decoration-2"
                : "text-white",
            )}
            style={{ textShadow: isBanned ? "none" : "0 2px 6px rgba(0,0,0,0.7)" }}
          >
            {character.name}
          </p>
        </div>

        {/* Banned overlay */}
        <AnimatePresence>
          {isBanned && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex flex-col items-center justify-center"
            >
              {/* X icon in top-right corner (non-color indicator) */}
              <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-red-500/80 flex items-center justify-center">
                <X size={10} strokeWidth={3} stroke="white" aria-hidden="true" />
              </div>

              {/* Diagonal strike lines */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                  className="absolute top-0 left-0 w-[150%] h-px bg-red-500/30 origin-top-left rotate-[30deg]"
                  style={{ top: "38%" }}
                />
                <div
                  className="absolute top-0 left-0 w-[150%] h-px bg-red-500/20 origin-top-left rotate-[30deg]"
                  style={{ top: "44%" }}
                />
              </div>

              <motion.div
                initial={{ scale: 0, rotate: -12 }}
                animate={{ scale: 1, rotate: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                className="relative"
              >
                <div className="px-3 py-1 bg-red-500/90 backdrop-blur-sm rounded-sm border border-red-400/30 shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                  <span className="font-display font-bold text-white text-xs uppercase tracking-wider">
                    Banned
                  </span>
                </div>
              </motion.div>

              {bannedByTeam != null && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-1.5 text-[10px] uppercase tracking-wider text-red-300/70 font-medium"
                >
                  by {bannedByTeamName ?? `Team ${bannedByTeam}`}
                  {bannedByTeam === myTeam ? " (you)" : ""}
                </motion.span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top accent line for active cards */}
        {!isBanned && isMyTurn && (
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        )}
      </div>
    </motion.button>
  );
}
